'use client';

import React, { useEffect, useRef, useState } from 'react';
import NextImage from 'next/image';
import {
    PortableText,
    type PortableTextBlock,
    type PortableTextReactComponents,
} from 'next-sanity';
import { getImageDimensions } from '@sanity/asset-utils';

import { parseChildrenToSlug } from '@/app/lib/utils/utils';
import { createSanityImageLoader, urlForImage } from '@/sanity/lib/image';
import { cn } from '@/app/lib/utils/utils';

const BLOG_CONTENT_MAX_IMAGE_WIDTH = 700;
const BLOG_CONTENT_IMAGE_MAX_SOURCE_WIDTH = 1440;

const getBlogContentImageQuality = (width: number) => {
    if (width <= 768) {
        return 56;
    }

    if (width <= 1200) {
        return 62;
    }

    return 68;
};

const isVisuallyEmpty = (node: React.ReactNode): boolean => {
    const list = React.Children.toArray(node);
    if (list.length === 0) return true;

    const isEmpty = (n: React.ReactNode): boolean => {
        if (typeof n === 'string') return n.replace(/[\s\u00A0]+/g, '') === '';
        if (typeof n === 'number') return String(n).trim() === '';
        if (Array.isArray(n)) return n.every(isEmpty);

        if (React.isValidElement(n)) {
            const el = n as React.ReactElement<{ children?: React.ReactNode }>;
            return isEmpty(el.props?.children ?? null);
        }

        return true;
    };

    return list.every(isEmpty);
};

const renderMarkedText = (
    children: React.ReactNode,
    value: any,
    isUnderline = false,
) => {
    const inputColor = value?.value || value?.hex || value?.color;
    const color = isUnderline ? 'inherit' : inputColor || 'inherit';

    const spanStyle: React.CSSProperties = { color };

    if (isUnderline) {
        spanStyle.textDecoration = 'underline';
        // 直接用 value 中指定的颜色作为下划线颜色；
        // 若无指定颜色（纯 underline mark），则跟随文字颜色（currentColor）
        spanStyle.textDecorationColor = inputColor || 'currentColor';
        spanStyle.textDecorationThickness = '0.1em';
        spanStyle.textDecorationStyle = 'dashed';
        spanStyle.textUnderlineOffset = '0.3em';
        spanStyle.textDecorationSkipInk = 'none';
    }

    return <span style={spanStyle}>{children}</span>;
};

function RichTextImage({ value }: Readonly<{ value: any }>) {
    if (!value?.asset) return null;

    const [imageLoaded, setImageLoaded] = useState(false);

    const align = value?.align || 'center';

    const rawScale = value?.scale;
    const parsedScale = rawScale ? parseInt(String(rawScale).trim(), 10) : 100;
    const scale = Math.min(Math.max(parsedScale, 50), 150);

    const getAlignmentClass = (alignment: string) => {
        switch (alignment) {
            case 'left':
                return 'flex justify-start';
            case 'right':
                return 'flex justify-end';
            case 'center':
            default:
                return 'flex justify-center';
        }
    };

    const dimensions = getImageDimensions(value);

    const scaledWidth = Math.round(dimensions.width * (scale / 100));
    const scaledHeight = Math.round(dimensions.height * (scale / 100));
    const imageAlt = value?.alt ?? '';
    const maxRenderedWidth = Math.min(
        scaledWidth,
        BLOG_CONTENT_MAX_IMAGE_WIDTH,
    );
    const maxSourceWidth = Math.min(
        scaledWidth,
        BLOG_CONTENT_IMAGE_MAX_SOURCE_WIDTH,
    );
    const imageUrl = urlForImage(value, {
        quality: getBlogContentImageQuality(maxRenderedWidth),
        width: maxSourceWidth,
    });
    const imageLoader = createSanityImageLoader(value, {
        fit: 'max',
        maxWidth: maxSourceWidth,
        resolveQuality: getBlogContentImageQuality,
    });

    useEffect(() => {
        setImageLoaded(false);
    }, [imageUrl]);

    return (
        <div className={`my-4 ${getAlignmentClass(align)}`}>
            <div
                className="relative max-w-full overflow-hidden"
                style={{ width: `min(100%, ${scaledWidth}px)` }}
            >
                {!imageLoaded && (
                    <div
                        className="absolute inset-0 animate-pulse rounded-[16px] bg-[#efefef]"
                        aria-hidden="true"
                    />
                )}
                <NextImage
                    loader={imageLoader}
                    src={imageUrl}
                    alt={imageAlt}
                    width={scaledWidth}
                    height={scaledHeight}
                    sizes={`(max-width: 767px) 87.5vw, ${maxRenderedWidth}px`}
                    className={cn(
                        'block h-auto max-w-full',
                        imageLoaded ? 'opacity-100' : 'opacity-0',
                    )}
                    loading="lazy"
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageLoaded(true)}
                />
            </div>
        </div>
    );
}

const components: Partial<PortableTextReactComponents> = {
    block: {
        normal: ({ children, value }) => {
            const empty = isVisuallyEmpty(children);
            const hasLink =
                Array.isArray(value?.markDefs) &&
                value.markDefs.some(
                    (def: any) =>
                        def._type === 'customLink' || def._type === 'link',
                );
            return (
                <p
                    className={cn(
                        'whitespace-break-spaces break-words py-[4px]',
                        hasLink && '!mb-[8px]',
                    )}
                    style={{ lineHeight: 'inherit', minInlineSize: '1ch' }}
                >
                    {empty ? '\u00A0' : children}
                </p>
            );
        },
        h1: ({ children, value }) => {
            // 补充处理 H1
            const slug = parseChildrenToSlug(value.children);
            return (
                <div
                    id={slug}
                    className="j-h1 tablet:pt-[20px] desktop:pt-[32px] pt-[16px]"
                >
                    {children}
                </div>
            );
        },
        h2: ({ children, value }) => {
            const slug = parseChildrenToSlug(value.children);
            return (
                <div
                    id={slug}
                    className="j-h2 tablet:pt-[20px] desktop:pt-[32px] pt-[16px]"
                >
                    {children}
                </div>
            );
        },
        h3: ({ children, value }) => {
            const slug = parseChildrenToSlug(value.children);
            return (
                <div
                    id={slug}
                    className="j-h3 tablet:pt-[20px] desktop:pt-[32px] pt-[16px]"
                >
                    {children}
                </div>
            );
        },
        h4: ({ children, value }) => {
            const slug = parseChildrenToSlug(value.children);
            return (
                <div
                    id={slug}
                    className="j-h4 tablet:pt-[20px] desktop:pt-[32px] pt-[16px]"
                >
                    {children}
                </div>
            );
        },
        h5: ({ children, value }) => {
            const slug = parseChildrenToSlug(value.children);
            return (
                <div
                    id={slug}
                    className="j-h5 tablet:pt-[20px] desktop:pt-[32px] pt-[16px]"
                >
                    {children}
                </div>
            );
        },
        h6: ({ children, value }) => {
            const slug = parseChildrenToSlug(value.children);
            return (
                <div
                    id={slug}
                    className="j-h6 tablet:pt-[20px] desktop:pt-[32px] pt-[16px]"
                >
                    {children}
                </div>
            );
        },
        blockquote: ({ children }) => (
            <blockquote className="border-[rgb(219, 219, 219)] text-black/55 my-[15px] border-l-[3px] pl-[10px] pt-1">
                {children}
            </blockquote>
        ),
    },
    list: {
        bullet: ({ children }) => (
            <ul className="my-5 ml-6 list-outside list-disc space-y-2">
                {children}
            </ul>
        ),
        number: ({ children }) => (
            <ol className="my-5 ml-6 list-outside list-decimal space-y-2">
                {children}
            </ol>
        ),
    },
    listItem: {
        bullet: ({ children }) => (
            <li className="pl-1 leading-relaxed">{children}</li>
        ),
        number: ({ children }) => (
            <li className="pl-1 leading-relaxed">{children}</li>
        ),
    },
    marks: {
        textColor: ({ children, value }) => {
            return renderMarkedText(children, value);
        },
        underlineColor: ({ children, value }) => {
            return renderMarkedText(children, value, true);
        },
        // 处理 Sanity 内置的 underline mark
        underline: ({ children }) => {
            return renderMarkedText(children, null, true);
        },
        code: ({ children }) => (
            <code className="rounded-md border border-white/10 bg-opacity-5 p-1 text-sm lg:whitespace-nowrap">
                {children}
            </code>
        ),
        customLink: ({ children, value }) => {
            const data = value?.customLink || value;
            const actualHref =
                data?.type === 'external' ? data?.external : data?.href;
            const target = data?.openInNewTab ? '_blank' : '_self';

            if (!actualHref || actualHref === '#') {
                return (
                    <span
                        style={{
                            textDecoration: 'none',
                            color: 'inherit',
                            fontWeight: 'inherit',
                        }}
                    >
                        {children || 'Link Broken'}
                    </span>
                );
            }

            return (
                <a
                    style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        fontWeight: 'inherit',
                    }}
                    href={actualHref}
                    aria-label={`Link to ${actualHref}`}
                    target={target}
                    rel={
                        target === '_blank' ? 'noopener noreferrer' : undefined
                    }
                >
                    {children}
                </a>
            );
        },
        strong: ({ children }) => (
            <strong
                style={{
                    color: 'inherit',
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                }}
            >
                {children}
            </strong>
        ),
        em: ({ children }) => (
            <em
                style={{
                    color: 'inherit',
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                }}
            >
                {children}
            </em>
        ),
    },
    types: {
        image: RichTextImage,
        styledBlock: ({ value }) => {
            const {
                alignment,
                fontSize,
                fontFamily,
                content,
                lineHeight,
                spaceBoth,
                spaceBefore,
                spaceAfter,
                letterSpacing,
            } = value;

            const fixedAlignment = alignment
                ?.trim()
                .replace(/[\u200B-\u200D\uFEFF]/g, '');

            const finalAlignment = [
                'left',
                'center',
                'right',
                'justify',
                'inherit',
            ].includes(fixedAlignment)
                ? (fixedAlignment as
                      | 'left'
                      | 'center'
                      | 'right'
                      | 'justify'
                      | 'inherit')
                : 'inherit';

            const fixedFontFamily = fontFamily
                ?.trim()
                .replace(/[\u200B-\u200D\uFEFF]/g, '');

            return (
                <div
                    style={{
                        marginTop: spaceBefore ? `${spaceBefore}px` : '0',
                        marginBottom: spaceAfter ? `${spaceAfter}px` : '0',
                    }}
                >
                    <PortableText
                        value={content}
                        components={{
                            ...components,
                            block: {
                                ...(components.block as Record<string, any>),
                                normal: ({ children, value }: any) => {
                                    const empty = isVisuallyEmpty(children);
                                    const hasLink =
                                        Array.isArray(value?.markDefs) &&
                                        value.markDefs.some(
                                            (def: any) =>
                                                def._type === 'customLink' ||
                                                def._type === 'link',
                                        );

                                    // ✨ 这里极大地精简了逻辑代码，并且使用了基于块级元素 div 的处理，保证文本对齐生效。
                                    const blockStyles: React.CSSProperties = {
                                        margin: 0,
                                        fontFamily:
                                            fixedFontFamily || 'inherit',
                                        fontSize: fontSize
                                            ? `${fontSize}px`
                                            : 'inherit',
                                        textAlign: finalAlignment,
                                        lineHeight: lineHeight
                                            ? `${lineHeight}em`
                                            : 'inherit',
                                        letterSpacing: letterSpacing
                                            ? `${letterSpacing}px`
                                            : 'inherit',
                                        marginLeft: spaceBoth
                                            ? `${spaceBoth}px`
                                            : '0',
                                        marginRight: spaceBoth
                                            ? `${spaceBoth}px`
                                            : '0',
                                    };

                                    return (
                                        <div
                                            className={cn(
                                                'whitespace-break-spaces break-words',
                                            )}
                                            style={blockStyles}
                                        >
                                            {!empty ? children : '\u00A0'}
                                        </div>
                                    );
                                },
                            },
                        }}
                    />
                </div>
            );
        },
        video: ({ value }: any) => {
            const videoUrl = value?.videoUrl;
            if (!videoUrl) return null;

            const align = value?.align || 'center';
            const caption = value?.caption;

            const getAlignmentClass = (alignment: string) => {
                switch (alignment) {
                    case 'left':
                        return 'flex justify-start';
                    case 'right':
                        return 'flex justify-end';
                    case 'center':
                    default:
                        return 'flex justify-center';
                }
            };

            return (
                <div className={`my-4 ${getAlignmentClass(align)}`}>
                    <video
                        src={videoUrl}
                        controls
                        className="w-full"
                        preload="metadata"
                    />
                </div>
            );
        },
    },
    hardBreak: () => <br />,
};

export function RichText<T = any>({
    richText,
    className,
}: Readonly<{
    richText?: T | null;
    className?: string;
}>): React.ReactElement | null {
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: ClipboardEvent) => {
            e.preventDefault();

            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) return;

            const range = selection.getRangeAt(0);
            const cloned = range.cloneContents();

            const tempDiv = document.createElement('div');
            tempDiv.appendChild(cloned);

            const allElements = tempDiv.querySelectorAll('*');
            for (const el of allElements) {
                if (el instanceof HTMLElement) {
                    el.removeAttribute('class');
                    el.removeAttribute('id');

                    const styleAttr = el.getAttribute('style');
                    if (styleAttr) {
                        const cleaned = styleAttr
                            .split(';')
                            .map((s) => s.trim())
                            .filter((s) => !s.startsWith('--tw-') && s !== '')
                            .join('; ');
                        if (cleaned) {
                            el.setAttribute('style', cleaned);
                        } else {
                            el.removeAttribute('style');
                        }
                    }
                }
            }

            const cleanedHTML = tempDiv.innerHTML;
            const cleanedText = tempDiv.textContent || '';

            e.clipboardData?.setData('text/html', cleanedHTML);
            e.clipboardData?.setData('text/plain', cleanedText);
        };

        const current = contentRef.current;
        if (current) {
            current.addEventListener('copy', handler);
        }

        return () => {
            if (current) {
                current.removeEventListener('copy', handler);
            }
        };
    }, []);

    if (!richText) return null;

    return (
        <div
            ref={contentRef}
            className={cn(
                'prose prose-zinc prose-headings:scroll-m-24 prose-headings:text-opacity-90 prose-p:text-opacity-80 prose-a:decoration-dotted prose-ol:text-opacity-80 prose-ul:text-opacity-80 prose-h2:pb-2 prose-h2:text-3xl prose-h2:font-semibold prose-h2:first:mt-0 dark:prose-invert max-w-none',
                className,
            )}
        >
            <PortableText
                value={richText as unknown as PortableTextBlock[]}
                components={components}
                onMissingComponent={(_, { nodeType, type }) =>
                    console.log('missing component', nodeType, type)
                }
            />
        </div>
    );
}
